import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import {
  ArrowSquareOut,
  BookOpen,
  CaretDown,
  CaretRight,
  Check,
  CheckCircle,
  Code,
  Copy,
  FlowArrow,
  Laptop,
  Lightning,
  Robot,
  Shield,
  Terminal,
  X
} from '@phosphor-icons/react';
/**
 * MCP Connection Guide Modal
 * Step-by-step instructions for connecting external MCP clients to Man & Machine
 */
const MCPConnectionGuide = ({ isOpen, onClose, token = null }) => {
  const [activeTab, setActiveTab] = useState('quickstart');
  const [copiedItem, setCopiedItem] = useState(null);
  const [expandedSection, setExpandedSection] = useState('claude');
  
  // Production URL
  const MCP_ENDPOINT = 'https://manandmachine.app/api/mcp';
  
  // Copy to clipboard helper
  const copyToClipboard = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Config templates
  const claudeConfig = `{
  "mcpServers": {
    "man-and-machine": {
      "url": "${MCP_ENDPOINT}",
      "transport": "http",
      "headers": {
        "Authorization": "Bearer ${token || 'YOUR_MCP_TOKEN_HERE'}"
      }
    }
  }
}`;

  const cursorConfig = `{
  "servers": {
    "man-and-machine": {
      "url": "${MCP_ENDPOINT}",
      "headers": {
        "Authorization": "Bearer ${token || 'YOUR_MCP_TOKEN_HERE'}"
      }
    }
  }
}`;

  const curlExample = `curl -X POST ${MCP_ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${token || 'YOUR_MCP_TOKEN_HERE'}" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/list",
    "params": {}
  }'`;

  const pythonExample = `import requests

MCP_ENDPOINT = "${MCP_ENDPOINT}"
TOKEN = "${token || 'YOUR_MCP_TOKEN_HERE'}"

# List available tools
response = requests.post(
    MCP_ENDPOINT,
    headers={
        "Content-Type": "application/json",
        "Authorization": f"Bearer {TOKEN}"
    },
    json={
        "jsonrpc": "2.0",
        "id": 1,
        "method": "tools/list",
        "params": {}
    }
)

tools = response.json()["result"]["tools"]
for tool in tools:
    print(f"- {tool['name']}: {tool['description']}")`;

  const jsExample = `const MCP_ENDPOINT = "${MCP_ENDPOINT}";
const TOKEN = "${token || 'YOUR_MCP_TOKEN_HERE'}";

// List available tools
const response = await fetch(MCP_ENDPOINT, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': \`Bearer \${TOKEN}\`
  },
  body: JSON.stringify({
    jsonrpc: '2.0',
    id: 1,
    method: 'tools/list',
    params: {}
  })
});

const { result } = await response.json();
console.log('Available tools:', result.tools);`;

  // Available tools data
  const availableTools = [
    { name: 'mm.list_capabilities', scope: 'mcp:read', desc: 'List all platform capabilities' },
    { name: 'mm.list_agents', scope: 'agent:read', desc: 'List all agents in your workspace' },
    { name: 'mm.get_agent', scope: 'agent:read', desc: 'Get detailed info about a specific agent' },
    { name: 'mm.run_agent_test', scope: 'agent:run_test', desc: 'Run agent in TEST mode (safe, no side effects)' },
    { name: 'mm.run_agent', scope: 'agent:run_prod', desc: 'Run agent in PRODUCTION mode (real side effects)' },
  ];

  // Scope explanations
  const scopes = [
    { id: 'mcp:read', color: 'blue', desc: 'List tools and basic MCP operations' },
    { id: 'agent:read', color: 'green', desc: 'List and read agent configurations' },
    { id: 'agent:run_test', color: 'amber', desc: 'Execute agents in safe test mode' },
    { id: 'agent:run_prod', color: 'red', desc: 'Execute agents with real side effects' },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#13141a] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-indigo-500/20 border border-indigo-500/30">
                <Lightning className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Connect to MCP Server</h2>
                <p className="text-xs text-slate-400">Connect Claude, Cursor, or any MCP client to Man & Machine</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10 px-4 bg-[#0d0e12]">
            {[
              { id: 'quickstart', label: 'Quick Start', icon: Lightning },
              { id: 'clients', label: 'Client Setup', icon: Laptop },
              { id: 'tools', label: 'Available Tools', icon: Robot },
              { id: 'code', label: 'Code Examples', icon: Code },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-indigo-400 border-indigo-400'
                    : 'text-slate-400 border-transparent hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Quick Start Tab */}
            {activeTab === 'quickstart' && (
              <div className="space-y-4">
                {/* Connection Info Card */}
                <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 font-semibold text-sm">Connection Details</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">Endpoint URL</div>
                        <code className="text-emerald-400 text-sm">{MCP_ENDPOINT}</code>
                      </div>
                      <button
                        onClick={() => copyToClipboard(MCP_ENDPOINT, 'endpoint')}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {copiedItem === 'endpoint' ? (
                          <Check className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">Transport</div>
                        <code className="text-white text-sm">Streamable HTTP (JSON-RPC 2.0)</code>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">Authentication</div>
                        <code className="text-white text-sm">Bearer Token (MCP API Token)</code>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-3">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <BookOpen className="w-4 h-4 text-indigo-400" />
                    3 Simple Steps
                  </h3>
                  
                  {[
                    {
                      step: 1,
                      title: 'Create an API Token',
                      desc: 'Click "+ Create Token" above and select the scopes you need',
                      highlight: !token
                    },
                    {
                      step: 2,
                      title: 'Copy your token',
                      desc: 'Save it securely - tokens are only shown once!',
                      highlight: false
                    },
                    {
                      step: 3,
                      title: 'Configure your MCP client',
                      desc: 'Add the endpoint and token to your client config',
                      highlight: false
                    },
                  ].map((item) => (
                    <div
                      key={item.step}
                      className={`flex gap-3 p-3 rounded-xl border transition-all ${
                        item.highlight
                          ? 'bg-amber-500/10 border-amber-500/30'
                          : 'bg-white/[0.02] border-white/10'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                        item.highlight ? 'bg-amber-500/20 text-amber-400' : 'bg-indigo-500/20 text-indigo-400'
                      }`}>
                        {item.step}
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm">{item.title}</div>
                        <div className="text-xs text-slate-400">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Token display if available */}
                {token && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-emerald-400 font-medium flex items-center gap-2 text-sm">
                        <Shield className="w-4 h-4" />
                        Your Token (copy now!)
                      </span>
                      <button
                        onClick={() => copyToClipboard(token, 'token')}
                        className="flex items-center gap-2 px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs transition-colors"
                      >
                        {copiedItem === 'token' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === 'token' ? 'Copied!' : 'Copy Token'}
                      </button>
                    </div>
                    <code className="text-xs text-emerald-300 bg-black/30 p-2 rounded block overflow-x-auto">
                      {token}
                    </code>
                  </div>
                )}
              </div>
            )}

            {/* Client Setup Tab */}
            {activeTab === 'clients' && (
              <div className="space-y-3">
                {/* Claude Desktop */}
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'claude' ? null : 'claude')}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center border border-orange-500/30">
                        <span className="text-base">🤖</span>
                      </div>
                      <div className="text-left">
                        <div className="text-white font-medium text-sm">Claude Desktop</div>
                        <div className="text-xs text-slate-400">Anthropic&apos;s desktop app</div>
                      </div>
                    </div>
                    {expandedSection === 'claude' ? (
                      <CaretDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <CaretRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  {expandedSection === 'claude' && (
                    <div className="p-3 border-t border-white/10 bg-black/20">
                      <div className="text-xs text-slate-400 mb-2">
                        Add to <code className="text-indigo-400">~/Library/Application Support/Claude/claude_desktop_config.json</code> (Mac) 
                        or <code className="text-indigo-400">%APPDATA%\Claude\claude_desktop_config.json</code> (Windows)
                      </div>
                      <div className="relative">
                        <pre className="bg-[#0d0e12] p-3 rounded-lg text-xs text-slate-300 overflow-x-auto border border-white/5">
                          {claudeConfig}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(claudeConfig, 'claude')}
                          className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                        >
                          {copiedItem === 'claude' ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cursor */}
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'cursor' ? null : 'cursor')}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/30">
                        <span className="text-base">⌨️</span>
                      </div>
                      <div className="text-left">
                        <div className="text-white font-medium text-sm">Cursor</div>
                        <div className="text-xs text-slate-400">AI-powered code editor</div>
                      </div>
                    </div>
                    {expandedSection === 'cursor' ? (
                      <CaretDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <CaretRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  {expandedSection === 'cursor' && (
                    <div className="p-3 border-t border-white/10 bg-black/20">
                      <div className="text-xs text-slate-400 mb-2">
                        Add to <code className="text-indigo-400">.cursor/mcp.json</code> in your project root
                      </div>
                      <div className="relative">
                        <pre className="bg-[#0d0e12] p-3 rounded-lg text-xs text-slate-300 overflow-x-auto border border-white/5">
                          {cursorConfig}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(cursorConfig, 'cursor')}
                          className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                        >
                          {copiedItem === 'cursor' ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* cURL */}
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'curl' ? null : 'curl')}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center border border-green-500/30">
                        <Terminal className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="text-left">
                        <div className="text-white font-medium text-sm">cURL / Terminal</div>
                        <div className="text-xs text-slate-400">Command line testing</div>
                      </div>
                    </div>
                    {expandedSection === 'curl' ? (
                      <CaretDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <CaretRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  {expandedSection === 'curl' && (
                    <div className="p-3 border-t border-white/10 bg-black/20">
                      <div className="text-xs text-slate-400 mb-2">
                        Test the connection with this command:
                      </div>
                      <div className="relative">
                        <pre className="bg-[#0d0e12] p-3 rounded-lg text-xs text-slate-300 overflow-x-auto border border-white/5">
                          {curlExample}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(curlExample, 'curl')}
                          className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                        >
                          {copiedItem === 'curl' ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Available Tools Tab */}
            {activeTab === 'tools' && (
              <div className="space-y-4">
                {/* Tools List */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                    <FlowArrow className="w-4 h-4 text-indigo-400" />
                    Available MCP Tools
                  </h3>
                  <div className="space-y-2">
                    {availableTools.map((tool) => (
                      <div
                        key={tool.name}
                        className="p-3 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <code className="text-indigo-400 font-medium text-sm">{tool.name}</code>
                            <p className="text-xs text-slate-400 mt-0.5">{tool.desc}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded border ${
                            tool.scope.includes('prod') 
                              ? 'bg-red-500/20 text-red-400 border-red-500/30'
                              : tool.scope.includes('test')
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                          }`}>
                            {tool.scope}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scopes Explanation */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-indigo-400" />
                    Token Scopes Explained
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {scopes.map((scope) => (
                      <div
                        key={scope.id}
                        className={`p-3 rounded-lg border bg-${scope.color}-500/10 border-${scope.color}-500/30`}
                        style={{
                          backgroundColor: `rgba(var(--${scope.color}-500), 0.1)`,
                        }}
                      >
                        <code className={`text-${scope.color}-400 text-sm font-medium`}>{scope.id}</code>
                        <p className="text-xs text-slate-400 mt-1">{scope.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Code Examples Tab */}
            {activeTab === 'code' && (
              <div className="space-y-6">
                {/* Python Example */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    🐍 Python
                  </h3>
                  <div className="relative">
                    <pre className="bg-[#0d0e12] p-4 rounded-lg text-sm text-slate-300 overflow-x-auto border border-white/5">
                      {pythonExample}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(pythonExample, 'python')}
                      className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      {copiedItem === 'python' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* JavaScript Example */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    ⚡ JavaScript / Node.js
                  </h3>
                  <div className="relative">
                    <pre className="bg-[#0d0e12] p-4 rounded-lg text-sm text-slate-300 overflow-x-auto border border-white/5">
                      {jsExample}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(jsExample, 'js')}
                      className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      {copiedItem === 'js' ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 bg-[#0d0e12] flex items-center justify-between">
            <a
              href="https://modelcontextprotocol.io/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-indigo-400 transition-colors"
            >
              <ArrowSquareOut className="w-4 h-4" />
              MCP Documentation
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
            >
              Got it!
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MCPConnectionGuide;
